import type {
  GetRequestsResponse,
  RequestTypeEnum,
  SuperAdminTabEnum,
} from "@nlc/shared-types";
import type { SortDescriptor } from "react-stately";

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { requestListKeys } from "@/hooks/queryKeys";
import { useUser } from "@/hooks/useUser";
import apiClient from "@/lib/api/client";

import type { RequestFilterType } from "../../types/filters";

import { mapSortColumnToBackend } from "../../utils/sortingUtils";

interface UseSuperAdminRequestsByTypeOptions {
  enabled?: boolean;
  filter?: RequestFilterType;
  limit?: number;
  page?: number;
  requestType: RequestTypeEnum;
  search?: string;
  sortDescriptor?: SortDescriptor;
  tab: SuperAdminTabEnum;
}

/**
 * Hook for fetching superadmin requests for a specific request type
 * This provides independent pagination and data management per request type
 */
export function useSuperAdminRequestsByType({
  enabled = true,
  filter = "all",
  limit = 10, // Default to 10 items per page as requested
  page = 1,
  requestType,
  search = "",
  sortDescriptor,
  tab,
}: UseSuperAdminRequestsByTypeOptions): UseQueryResult<GetRequestsResponse> {
  const { data: userData } = useUser();

  return useQuery({
    enabled: !!userData?.user && enabled,
    gcTime: 15 * 60 * 1000, // 15 minutes garbage collection
    queryFn: async () => {
      // Build query parameters for individual request type
      const queryParams: Record<string, boolean | number | string | string[]> =
        {
          category: [requestType], // Filter for specific request type only
          direction:
            sortDescriptor?.direction === "descending" ? "desc" : "asc",
          // Request all needed fields for superadmin tabs
          fields: [
            "id",
            "name",
            "request_type",
            "status",
            "created_by",
            "assignee",
            "brand",
            "account",
            "created_at",
            "due_on",
            "design_type",
            "complexity_id",
            "rating",
            "completed_date",
          ],
          limit: limit || 10,
          page: page || 1,
          skipCount: true, // Now using /counts endpoint
          sortBy: sortDescriptor?.column
            ? mapSortColumnToBackend(String(sortDescriptor.column))
            : "createdAt",
          tab,
        };

      // Add search if provided
      if (search) {
        queryParams.search = search;
      }

      return await apiClient
        .url("/requests/list")
        .query(queryParams)
        .get()
        .json<GetRequestsResponse>();
    },
    queryKey: [
      ...requestListKeys.all,
      "superadmin-requests-by-type",
      tab,
      requestType,
      filter,
      search,
      sortDescriptor?.column || "createdAt",
      sortDescriptor?.direction || "desc",
      page,
      limit,
    ],
    refetchInterval: (data) =>
      data && document.visibilityState === "visible" ? 60 * 1000 : false,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}
